import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Globe2, Users, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useCountriesWithCities } from '@/hooks/useCountriesWithCities';
import { useAllCitiesCount } from '@/hooks/useCitiesFromDatabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

const CountriesListPage = () => {
  const { data: countriesData, isLoading, error } = useCountriesWithCities();
  const { data: totalCitiesCount } = useAllCitiesCount();
  const [expandedCountries, setExpandedCountries] = useState<Set<string>>(new Set());

  const toggleCountry = (country: string) => {
    setExpandedCountries(prev => {
      const next = new Set(prev);
      if (next.has(country)) {
        next.delete(country);
      } else {
        next.add(country);
      }
      return next;
    });
  };

  const totalCountries = countriesData?.length || 0;

  return (
    <>
      <Helmet>
        <title>Countries Directory - Find Therapists Worldwide | Global Therapy Hub</title>
        <meta name="description" content={`Access mental health professionals in ${totalCountries} countries. Our global directory connects you with certified psychologists and therapists worldwide.`} />
        <meta name="keywords" content="therapist by country, international psychologists, global mental health, therapy worldwide" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Global Mental Health Network
          </h1>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-8">
            Connect with certified psychologists, psychiatrists, and psychotherapists across the world. 
            Our directory spans {totalCountries} countries with {totalCitiesCount?.toLocaleString() || '32,000+'} cities worldwide.
          </p>
          
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Globe2 className="h-8 w-8 text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold">{totalCountries}</div>
                  <div className="text-sm text-muted-foreground">Countries</div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold">{totalCitiesCount?.toLocaleString() || '32,719'}</div>
                  <div className="text-sm text-muted-foreground">Cities</div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <Users className="h-8 w-8 text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold">10,000+</div>
                  <div className="text-sm text-muted-foreground">Professionals</div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="text-center">
                  <MapPin className="h-8 w-8 text-primary mx-auto mb-2" />
                  <div className="text-3xl font-bold">195+</div>
                  <div className="text-sm text-muted-foreground">Regions</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-destructive">Error loading countries. Please try again.</p>
          </div>
        )}

        {/* Countries List */}
        {countriesData && (
          <div className="space-y-3">
            <h2 className="text-2xl font-bold mb-6">
              Browse by Country
              <Badge variant="secondary" className="ml-2">{totalCountries} countries</Badge>
            </h2>
            
            {countriesData.map((countryData) => (
              <Card key={countryData.country} className="overflow-hidden">
                <Collapsible
                  open={expandedCountries.has(countryData.country)}
                  onOpenChange={() => toggleCountry(countryData.country)}
                >
                  <CardHeader className="cursor-pointer hover:bg-accent/50 transition-colors">
                    <CollapsibleTrigger className="w-full">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Globe2 className="h-5 w-5 text-primary" />
                          <CardTitle className="text-xl">{countryData.country}</CardTitle>
                          <Badge variant="secondary">
                            {countryData.cityCount} {countryData.cityCount === 1 ? 'city' : 'cities'}
                          </Badge>
                        </div>
                        {expandedCountries.has(countryData.country) ? (
                          <ChevronUp className="h-5 w-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-muted-foreground" />
                        )}
                      </div>
                    </CollapsibleTrigger>
                  </CardHeader>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        {countryData.cities.map((city) => (
                          <Link
                            key={city.slug}
                            to={`/city/${city.slug}`}
                            className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent transition-colors text-sm"
                          >
                            <MapPin className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            <span className="flex flex-col truncate">
                              <span className="font-medium">{city.name}</span>
                              {city.state_province && (
                                <span className="text-xs text-muted-foreground">{city.state_province}</span>
                              )}
                            </span>
                          </Link>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold mb-4">
            Can't Find Your Country?
          </h2>
          <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
            We're constantly expanding our global network. If your country isn't listed yet, 
            let us know and we'll prioritize adding mental health professionals in your region.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="min-w-[200px]">
                Request Your Country
              </Button>
            </Link>
            <Link to="/therapist-registration">
              <Button size="lg" variant="outline" className="min-w-[200px]">
                Join as a Professional
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default CountriesListPage;
